import {createSelector} from '@reduxjs/toolkit';

import {BADGE_MEMBER_STATUSES} from '@/consts/loyalty/badgeMemberStatuses';
import {BADGE_STATUSES} from '@/consts/loyalty/badgeStatuses';
import {MEMBER_REWARD_STATUSES} from '@/consts/loyalty/rewardStatuses';
import type {RootState} from '@/state/store';
import type {
    Badge,
    BadgeAction,
    BadgeReward,
    Journey,
    JourneyLevel,
    JourneyLevelBadge,
    MemberReward,
    Reward,
    UiReward,
} from '@/types/loyalty';
import {
    getBadgeImageByViewType,
    getJourneyBadgeViewType,
    getJourneyInfo,
    isJourneyCompleted,
    isJourneyInProgress,
    isJourneyLevelCompleted,
} from '@/utils/journeysUtils';

const selectBadgesData = (state: RootState) => state.loyaltySlice.badgesData;

const selectActiveJourneys = createSelector(
    [selectBadgesData],
    (badgesData): Journey[] => badgesData?.data?.journeys ?? []
);

const selectMemberJourneys = createSelector(
    [selectBadgesData],
    (badgesData): Journey[] => badgesData?.data?.memberJourneys ?? []
);

const selectInProgressJourneys = createSelector([selectMemberJourneys], (memberJourneys) =>
    memberJourneys.filter(isJourneyInProgress)
);

const selectCompletedJourneys = createSelector([selectMemberJourneys], (memberJourneys) =>
    memberJourneys.filter(isJourneyCompleted)
);

const resolveBadgeStatus = (journey: Journey, currentLevel: JourneyLevel): Badge['status'] => {
    if (isJourneyCompleted(journey)) return BADGE_STATUSES.COMPLETED;
    if (isJourneyInProgress(journey)) {
        // Multi-level: in-progress as soon as the journey itself is in progress.
        if ((journey.journeyLevels?.length ?? 0) > 1) return BADGE_STATUSES.IN_PROGRESS;
        // Single-level: in-progress only if the level itself has any progress.
        const total = currentLevel.memberJourneyActions?.length ?? 0;
        const left = currentLevel.actionsLeft ?? total;
        return left < total ? BADGE_STATUSES.IN_PROGRESS : BADGE_STATUSES.LOCKED;
    }
    return BADGE_STATUSES.LOCKED;
};

function mapJourneyRewards(level: JourneyLevel): BadgeReward[] {
    return (level.memberJourneyRewards ?? level.journeyRewards ?? [])
        .filter((r) => Boolean(r.rewardId))
        .map((r) => ({
            id: r.rewardId!,
            name: r.rewardName || undefined,
            imageUrl: r.rewardImage?.[0]?.imageURL || undefined,
            url: r.rewardUrl || undefined,
        }));
}

const BADGE_STATUS_TO_VIEW_TYPE = {
    [BADGE_STATUSES.COMPLETED]: BADGE_MEMBER_STATUSES.ACHIEVED,
    [BADGE_STATUSES.IN_PROGRESS]: BADGE_MEMBER_STATUSES.IN_PROGRESS,
    [BADGE_STATUSES.LOCKED]: BADGE_MEMBER_STATUSES.LOCKED,
} as const;

const mapLevelToJourneyLevelBadge = (level: JourneyLevel, status: Badge['status']): JourneyLevelBadge | null => {
    const levelBadge = level.memberJourneyBadge ?? level.journeyBadge;
    if (!levelBadge?.badgeId) return null;

    const viewType = BADGE_STATUS_TO_VIEW_TYPE[status];
    const imageUrl = getBadgeImageByViewType(levelBadge, viewType)?.imageURL;

    const rawActions = level.memberJourneyActions ?? level.journeyActions ?? [];
    const actionsLeft = level.actionsLeft ?? rawActions.length;
    const completedCount = Math.max(0, rawActions.length - actionsLeft);

    const actions: BadgeAction[] = rawActions.map((action, index) => ({
        id: action.actionId,
        label: action.actionLabel,
        description: action.actionDescription,
        url: action.actionURL || undefined,
        isCompleted: index < completedCount,
    }));

    const rewards = mapJourneyRewards(level);

    return {
        badgeId: levelBadge.badgeId,
        displayName: levelBadge.displayName,
        badgeDescription: levelBadge.badgeDescription || undefined,
        imageUrl: imageUrl || undefined,
        status,
        actions: actions.length > 0 ? actions : undefined,
        totalSteps: rawActions.length > 0 ? rawActions.length : undefined,
        finishedSteps: rawActions.length > 0 ? completedCount : undefined,
        rewards: rewards.length > 0 ? rewards : undefined,
    };
};

const resolveLevelStatuses = (journey: Journey): Badge['status'][] => {
    const isMemberJourney = Boolean(journey.memberJourneyStatus);
    const levels = journey.journeyLevels ?? [];

    let currentLevelFound = false;

    return levels.map((level) => {
        if (!isMemberJourney) return BADGE_STATUSES.LOCKED;
        if (isJourneyLevelCompleted(level)) return BADGE_STATUSES.COMPLETED;
        if (!currentLevelFound) {
            currentLevelFound = true;
            return BADGE_STATUSES.IN_PROGRESS;
        }
        return BADGE_STATUSES.LOCKED;
    });
};

const mapJourneyToBadge = (journey: Journey): Badge | null => {
    const {currentLevel, currentLevelBadge, progressCompleted, progressTotal, isSingleLevel} = getJourneyInfo(journey);
    if (!currentLevelBadge?.badgeId) return null;

    const expireAt = journey.memberJourneyExpireAt ?? journey.journeyExpireAt;

    const viewType = getJourneyBadgeViewType(journey);
    const imageUrl = getBadgeImageByViewType(currentLevelBadge, viewType)?.imageURL;
    const achievedImageUrl = getBadgeImageByViewType(currentLevelBadge, 'Achieved')?.imageURL;

    const rawActions = currentLevel.memberJourneyActions ?? currentLevel.journeyActions ?? [];
    // Derive completion from `actionsLeft` instead of per-action status. The
    // backend orders actions sequentially (completed ones first), so the first
    // `total - actionsLeft` items are considered done. For active (non-member)
    // journeys `actionsLeft` is absent → nothing is completed yet.
    const totalActions = rawActions.length;
    const actionsLeft = currentLevel.actionsLeft ?? totalActions;
    const completedCount = Math.max(0, totalActions - actionsLeft);
    const actions: BadgeAction[] = rawActions.map((action, index) => ({
        id: action.actionId,
        label: action.actionLabel,
        description: action.actionDescription,
        url: action.actionURL || undefined,
        isCompleted: index < completedCount,
    }));

    const levelStatuses = isSingleLevel ? [] : resolveLevelStatuses(journey);
    const journeyLevels = isSingleLevel
        ? undefined
        : (journey.journeyLevels ?? [])
              .map((level, i) => mapLevelToJourneyLevelBadge(level, levelStatuses[i] ?? BADGE_STATUSES.LOCKED))
              .filter((l): l is JourneyLevelBadge => l !== null);

    const allRewards = isSingleLevel
        ? mapJourneyRewards(currentLevel)
        : (journey.journeyLevels ?? []).flatMap(mapJourneyRewards);

    return {
        id: currentLevelBadge.badgeId,
        journeyId: journey.journeyId,
        title: journey.journeyName ?? '',
        description: journey.journeyDescription || undefined,
        extendedDescription: currentLevelBadge.badgeExtendedDescription || undefined,
        status: resolveBadgeStatus(journey, currentLevel),
        expireAt: expireAt ? new Date(expireAt) : undefined,
        imageUrl,
        achievedImageUrl,
        totalSteps: progressTotal > 0 ? progressTotal : undefined,
        finishedSteps: progressTotal > 0 ? Math.max(0, progressCompleted) : undefined,
        actions: actions.length > 0 ? actions : undefined,
        isSingleLevel,
        isNewBadge: journey.isNewBadge ?? undefined,
        tagName: currentLevelBadge.badgeTags?.[0]?.tagValue || undefined,
        journeyLevels: journeyLevels?.length ? journeyLevels : undefined,
        rewards: allRewards.length > 0 ? allRewards : undefined,
    };
};

const journeysToBadges = (journeys: Journey[]): Badge[] =>
    journeys.map(mapJourneyToBadge).filter((b): b is Badge => b !== null);

/**
 * "All badges" — in-progress + completed member journeys, then active (locked)
 * journeys. Order matches legacy UWM. Multi-level journeys collapse into a
 * single badge representing the current level.
 */
export const selectAllBadges = createSelector(
    [selectInProgressJourneys, selectCompletedJourneys, selectActiveJourneys],
    (inProgress, completed, active) => journeysToBadges([...inProgress, ...completed, ...active])
);

/** In-progress tab — only member journeys with status `In Progress`. */
export const selectInProgressBadges = createSelector([selectInProgressJourneys], journeysToBadges);

/** Available badges — in-progress + locked (active) journeys, excluding completed ones. */
export const selectAvailableBadges = createSelector(
    [selectInProgressJourneys, selectActiveJourneys],
    (inProgress, active) => journeysToBadges([...inProgress, ...active])
);

// ---- Rewards ----

const selectRewardsData = (state: RootState) => state.loyaltySlice.rewardsData;

const selectMemberRewards = createSelector(
    [selectRewardsData],
    (rewardsData): MemberReward[] => rewardsData?.data?.memberRewards ?? []
);

const selectCatalogRewards = createSelector(
    [selectRewardsData],
    (rewardsData): Reward[] => rewardsData?.data?.rewards ?? []
);

const mapMemberRewardToUi = (reward: MemberReward): UiReward | null => {
    const id = reward.rewardId;
    if (!id) return null;
    return {
        id,
        title: reward.rewardName || undefined,
        description: reward.rewardDescription || undefined,
        imageUrl: reward.rewardImage?.[0]?.imageURL,
        url: reward.rewardUrl || undefined,
        redeemedDate: reward.rewardRedeemedDate,
        journeyId: reward.journeyId,
    };
};

const mapCatalogRewardToUi = (reward: Reward): UiReward | null => {
    const id = reward.rewardId;
    if (!id) return null;
    return {
        id,
        title: reward.rewardName || undefined,
        description: reward.rewardDescription || undefined,
        imageUrl: reward.rewardImage?.[0]?.imageURL,
        url: reward.rewardUrl || undefined,
        journeyId: reward.journeyId,
    };
};

const compact = <T>(items: Array<T | null>): T[] => items.filter((x): x is T => x !== null);

/** Available tab — rewards the member has earned but not yet redeemed (Awarded and Eligible). */
export const selectAvailableRewards = createSelector([selectMemberRewards], (memberRewards) =>
    compact(
        memberRewards
            .filter(
                (r) =>
                    r.memberRewardStatus === MEMBER_REWARD_STATUSES.AWARDED ||
                    r.memberRewardStatus === MEMBER_REWARD_STATUSES.ELIGIBLE
            )
            .map(mapMemberRewardToUi)
    )
);

/** Not earned tab — catalog rewards plus locked member rewards. */
export const selectNotEarnedRewards = createSelector(
    [selectCatalogRewards, selectMemberRewards],
    (catalogRewards, memberRewards) => {
        const catalogItems = compact(catalogRewards.map(mapCatalogRewardToUi));
        const lockedItems = compact(
            memberRewards.filter((r) => r.memberRewardStatus === MEMBER_REWARD_STATUSES.LOCKED).map(mapMemberRewardToUi)
        );
        return [...catalogItems, ...lockedItems];
    }
);

/** Redeemed tab — rewards the member has already redeemed. */
export const selectRedeemedRewards = createSelector([selectMemberRewards], (memberRewards) =>
    compact(
        memberRewards.filter((r) => r.memberRewardStatus === MEMBER_REWARD_STATUSES.REDEEMED).map(mapMemberRewardToUi)
    )
);
